import { useState, useEffect } from 'react';
import { DashboardHeader } from '@/components/DashboardHeader';
import { ScrapbookCard } from '@/components/ScrapbookCard';
import { CreateScrapbookModal } from '@/components/CreateScrapbookModal';
import { ShareModal } from '@/components/ShareModal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { api } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { TIER_LIMITS } from '@shared/constants';
import type { ScrapbookSummary, PaginatedResponse } from '@shared/types';
import './DashboardPage.css';

const ITEMS_PER_PAGE = 12;

export function DashboardPage() {
  const [scrapbooks, setScrapbooks] = useState<ScrapbookSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalScrapbooks, setTotalScrapbooks] = useState(0);

  // Get user from auth store
  const user = useAuthStore((state) => state.user);
  const tierLimits = TIER_LIMITS[user?.subscriptionTier ?? 'free'];
  const isAtScrapbookLimit = totalScrapbooks >= tierLimits.maxScrapbooks;

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [shareModalScrapbookId, setShareModalScrapbookId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Fetch scrapbooks on mount
  useEffect(() => {
    fetchScrapbooks(1);
  }, []);

  const fetchScrapbooks = async (pageNum: number) => {
    if (pageNum === 1) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setError(null);

    try {
      const response = await api.get<PaginatedResponse<ScrapbookSummary>>(
        '/scrapbooks',
        {
          page: String(pageNum),
          limit: String(ITEMS_PER_PAGE),
          sort: 'updated_desc',
        }
      );

      // Filter out any undefined/null items to prevent "Cannot read properties of undefined" errors
      const validScrapbooks = (response.data || []).filter(
        (s): s is ScrapbookSummary => s != null && typeof s === 'object' && 'id' in s
      );

      if (pageNum === 1) {
        setScrapbooks(validScrapbooks);
      } else {
        setScrapbooks((prev) => {
          const validPrev = prev.filter(
            (s): s is ScrapbookSummary => s != null && typeof s === 'object' && 'id' in s
          );
          return [...validPrev, ...validScrapbooks];
        });
      }

      setHasMore(response.hasMore);
      setPage(pageNum);
      setTotalScrapbooks(response.total);
    } catch (err) {
      setError('Failed to load scrapbooks. Please try again.');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!isLoadingMore && hasMore) {
      fetchScrapbooks(page + 1);
    }
  };

  const handleShare = (id: string) => {
    setShareModalScrapbookId(id);
  };

  const handleDeleteRequest = (id: string) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;

    setIsDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/scrapbooks/${deleteConfirmId}`);
      setScrapbooks((prev) => prev.filter(
        (s): s is ScrapbookSummary => s != null && s.id !== deleteConfirmId
      ));
      setTotalScrapbooks((prev) => Math.max(0, prev - 1));
      setDeleteConfirmId(null);
    } catch (err) {
      // Show error inside the dialog
      setDeleteError('Failed to delete. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
    setDeleteError(null);
  };

  const handleScrapbookCreated = (scrapbook: ScrapbookSummary) => {
    // Validate scrapbook before adding to prevent undefined items in array
    if (scrapbook && scrapbook.id) {
      setScrapbooks((prev) => [scrapbook, ...prev]);
      setTotalScrapbooks((prev) => prev + 1);
    }
  };

  // Use optional chaining to prevent crash if array contains undefined items
  const scrapbookToDelete = scrapbooks.find((s) => s?.id === deleteConfirmId);

  return (
    <div className="dashboard">
      <DashboardHeader />

      <main className="dashboard-main">
        <div className="dashboard-content">
          {/* Header with title and create button */}
          <div className="dashboard-title-bar">
            <h1 className="dashboard-title">My Scrapbooks</h1>
            {isAtScrapbookLimit ? (
              <div className="scrapbook-limit-notice">
                <span className="limit-text">
                  {tierLimits.maxScrapbooks} of {tierLimits.maxScrapbooks} scrapbooks used
                </span>
                <a href="/pricing" className="btn btn-primary">
                  Upgrade to Pro
                </a>
              </div>
            ) : (
              <button
                className="btn btn-primary"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M8 3V13M3 8H13"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                New Scrapbook
              </button>
            )}
          </div>

          {/* Loading state */}
          {isLoading && (
            <div className="dashboard-loading">
              <LoadingSpinner size="lg" />
              <p>Loading your scrapbooks...</p>
            </div>
          )}

          {/* Error state */}
          {error && !isLoading && (
            <div className="dashboard-error" role="alert">
              <svg
                width="48"
                height="48"
                viewBox="0 0 48 48"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M24 16V26M24 32V32.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <p>{error}</p>
              <button
                className="btn btn-secondary"
                onClick={() => fetchScrapbooks(1)}
              >
                Try Again
              </button>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && scrapbooks.length === 0 && (
            <div className="dashboard-empty">
              <svg
                width="120"
                height="120"
                viewBox="0 0 120 120"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="20"
                  y="20"
                  width="80"
                  height="80"
                  rx="8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeDasharray="6 4"
                />
                <path
                  d="M60 45V75M45 60H75"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <h2>No scrapbooks yet</h2>
              <p>Create your first scrapbook to start designing!</p>
              <button
                className="btn btn-primary btn-lg"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M8 3V13M3 8H13"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                Create Your First Scrapbook
              </button>
            </div>
          )}

          {/* Scrapbook grid */}
          {!isLoading && !error && scrapbooks.length > 0 && (
            <>
              <div className="dashboard-grid">
                {scrapbooks
                  .filter((s): s is ScrapbookSummary => s != null && !!s.id)
                  .map((scrapbook) => (
                    <ScrapbookCard
                      key={scrapbook.id}
                      scrapbook={scrapbook}
                      onDelete={handleDeleteRequest}
                      onShare={handleShare}
                    />
                  ))}
              </div>

              {/* Load more button */}
              {hasMore && (
                <div className="dashboard-load-more">
                  <button
                    className="btn btn-secondary"
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? (
                      <>
                        <LoadingSpinner size="sm" />
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Modals */}
      <CreateScrapbookModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleScrapbookCreated}
      />

      {shareModalScrapbookId && (
        <ShareModal
          scrapbookId={shareModalScrapbookId}
          isOpen={!!shareModalScrapbookId}
          onClose={() => setShareModalScrapbookId(null)}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteConfirmId}
        title="Delete Scrapbook"
        message={`Are you sure you want to delete "${scrapbookToDelete?.title || 'this scrapbook'}"? This action cannot be undone.`}
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
        variant="danger"
        isConfirmDisabled={isDeleting}
        error={deleteError}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
}
